import React from "react";
import { Card, Progress, Spin, Button } from "antd";

// Table Skeleton
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => (
  <div className="w-full">
    {/* Header */}
    <div className="flex gap-2 mb-4">
      {Array.from({ length: columns }).map((_, index) => (
        <div
          key={index}
          className="flex-1 h-10 bg-gray-200 rounded-md animate-pulse"
        />
      ))}
    </div>

    {/* Rows */}
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="flex gap-2 mb-2">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <div
            key={colIndex}
            className={`flex-1 h-8 rounded animate-pulse ${
              rowIndex % 2 === 0 ? "bg-gray-100" : "bg-gray-50"
            }`}
          />
        ))}
      </div>
    ))}
  </div>
);

// Form Skeleton
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 6 }) => (
  <div className="w-full space-y-6">
    {Array.from({ length: fields }).map((_, index) => (
      <div key={index} className="space-y-2">
        <div className="w-2/5 h-5 bg-gray-200 rounded animate-pulse" />
        <div className="w-full h-14 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    ))}
  </div>
);

// Card Skeleton
export const CardSkeleton: React.FC<{ showHeader?: boolean }> = ({
  showHeader = true,
}) => (
  <Card className="mb-6 card-shadow">
    {showHeader && (
      <div className="mb-4 space-y-2">
        <div className="w-3/5 h-8 bg-gray-200 rounded animate-pulse" />
        <div className="w-2/5 h-5 bg-gray-100 rounded animate-pulse" />
      </div>
    )}
    <FormSkeleton fields={4} />
  </Card>
);

// Calculation Progress
interface CalculationProgressProps {
  stage: string;
  progress: number;
  currentItem: string;
}

export const CalculationProgress: React.FC<CalculationProgressProps> = ({
  stage,
  progress,
  currentItem,
}) => (
  <div className="fade-in">
    <Card className="mb-6 text-center bg-gradient-to-br from-blue-50 to-indigo-100 border-0 shadow-lg">
      <div className="flex flex-col items-center space-y-4">
        <Spin size="large" className="text-primary-800" />

        <h3 className="text-xl font-semibold text-primary-800">{stage}</h3>

        <p className="text-gray-600 text-sm">{currentItem}</p>

        <div className="w-full max-w-md">
          <Progress
            percent={progress}
            strokeColor={{
              "0%": "#667eea",
              "100%": "#764ba2",
            }}
            strokeWidth={8}
            className="mb-2"
          />
        </div>

        <span className="text-xs text-gray-500">
          {Math.round(progress)}% Complete
        </span>
      </div>
    </Card>
  </div>
);

// Page Loading
export const PageLoading: React.FC<{ message?: string }> = ({
  message = "Loading...",
}) => (
  <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4">
    <Spin size="large" />
    <p className="text-gray-600">{message}</p>
  </div>
);

// Section Loading
export const SectionLoading: React.FC<{
  title: string;
  showCards?: number;
}> = ({ showCards = 2 }) => (
  <div className="max-w-7xl mx-auto mt-4">
    <div className="w-2/5 h-12 bg-gray-200 rounded animate-pulse mb-6" />

    {Array.from({ length: showCards }).map((_, index) => (
      <CardSkeleton key={index} />
    ))}
  </div>
);

// Button Loading
export const ButtonLoading: React.FC<{
  size?: "small" | "default" | "large";
}> = ({ size = "default" }) => <Spin size={size} />;

// Data Loading with Retry
interface DataLoadingProps {
  message: string;
  onRetry?: () => void;
  showRetry?: boolean;
}

export const DataLoading: React.FC<DataLoadingProps> = ({
  message,
  onRetry,
  showRetry = false,
}) => (
  <Card className="text-center py-8">
    <div className="flex flex-col items-center space-y-4">
      <Spin size="large" />
      <p className="text-gray-600">{message}</p>
      {showRetry && onRetry && (
        <Button
          type="link"
          onClick={onRetry}
          className="text-primary-600 hover:text-primary-800"
        >
          Retry
        </Button>
      )}
    </div>
  </Card>
);

// Smooth transition wrapper
interface SmoothTransitionProps {
  children: React.ReactNode;
  loading: boolean;
  fallback?: React.ReactNode;
}

export const SmoothTransition: React.FC<SmoothTransitionProps> = ({
  children,
  loading,
  fallback,
}) => (
  <div className="relative min-h-[200px]">
    <div
      className={`transition-opacity duration-300 ${
        loading ? "opacity-0 pointer-events-none" : "opacity-100"
      }`}
    >
      {children}
    </div>

    {loading && (
      <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300">
        {fallback || <PageLoading />}
      </div>
    )}
  </div>
);
